// 6 sectors × 500 procedural levels = 3000 total.
// Each level is generated deterministically from its global index so players
// on the same level see the same layout.

export const COLS = 12;
export const ROWS = 8;

export const SECTORS = [
  {
    id: "beginner",
    name: "Beginner",
    from: 1,
    to: 500,
    accent: "#00ffff",
    speedRange: [3.5, 5.2],
    tierWeights: [0.85, 0.15, 0, 0], // tier1, tier2, tier3, tier4
    densityRange: [0.35, 0.7],
    skin: "beginner-victor",
    skinName: "Azure Initiate",
  },
  {
    id: "easy",
    name: "Easy",
    from: 501,
    to: 1000,
    accent: "#00ff41",
    speedRange: [5.0, 6.0],
    tierWeights: [0.55, 0.35, 0.1, 0],
    densityRange: [0.45, 0.78],
    skin: "easy-champion",
    skinName: "Emerald Champion",
  },
  {
    id: "medium",
    name: "Medium",
    from: 1001,
    to: 1500,
    accent: "#ffd700",
    speedRange: [5.6, 6.8],
    tierWeights: [0.35, 0.4, 0.22, 0.03],
    densityRange: [0.55, 0.85],
    skin: "medium-conqueror",
    skinName: "Solar Conqueror",
  },
  {
    id: "hard",
    name: "Hard",
    from: 1501,
    to: 2000,
    accent: "#ff8c1a",
    speedRange: [6.3, 7.4],
    tierWeights: [0.15, 0.4, 0.4, 0.05],
    densityRange: [0.65, 0.9],
    skin: "hard-warrior",
    skinName: "Ember Warrior",
  },
  {
    id: "expert",
    name: "Expert",
    from: 2001,
    to: 2500,
    accent: "#ff007f",
    speedRange: [7.0, 8.2],
    tierWeights: [0.05, 0.25, 0.55, 0.15],
    densityRange: [0.75, 0.95],
    skin: "expert-master",
    skinName: "Crimson Master",
  },
  {
    id: "legendary",
    name: "Legendary",
    from: 2501,
    to: 3000,
    accent: "#b026ff",
    speedRange: [7.8, 9.5],
    tierWeights: [0, 0.1, 0.55, 0.35],
    densityRange: [0.85, 1.0],
    skin: "legendary-ascended",
    skinName: "Void Ascendant",
  },
];

export const TOTAL_LEVELS = SECTORS[SECTORS.length - 1].to;
export const SECTOR_SIZE = 500;

export function getSector(globalLevel) {
  return (
    SECTORS.find((s) => globalLevel >= s.from && globalLevel <= s.to) || SECTORS[0]
  );
}

// --- seeded PRNG ------------------------------------------------------------
function mulberry32(seed) {
  return function rand() {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromString(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function weightedTier(rand, weights) {
  const r = rand();
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    acc += weights[i];
    if (r < acc) return i + 1; // tier 1..4
  }
  return 1;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// --- Space-themed shape library --------------------------------------------
// Each shape is an 8-row × 12-col ASCII template. '#' = block present,
// '.' = empty. Shapes are composed into a level by `getLevel` — the tier of
// each '#' is then sampled from the sector's tierWeights. This gives every
// procedural level recognisable space iconography (rocket, saucer, ringed
// planet, constellation, etc.) instead of uniform grids.

const SHAPES_RAW = {
  // Classic invader: the shape featured in the earlier user reference.
  diamond: [
    "............",
    ".##########.",
    "..########..",
    "...######...",
    "....####....",
    ".....##.....",
    "............",
    "............",
  ],
  // Stacked layered invader — inverted pyramid on top + notched bar below.
  // This is the layout shown in the latest user-provided reference.
  invaderStack: [
    "............",
    ".##########.",
    "..########..",
    "...######...",
    "............",
    ".##########.",
    ".####..####.",
    "............",
  ],
  // Saturn — wide flat ring around a small centre body.
  saturn: [
    "............",
    "............",
    "####....####",
    ".##########.",
    ".####..####.",
    "..########..",
    "............",
    "............",
  ],
  // Nebula — wide sparse cloud with soft edges.
  nebula: [
    "............",
    "..#..##..#..",
    ".###....###.",
    "####....####",
    ".###....###.",
    "..#..##..#..",
    "............",
    "............",
  ],
  // Pulsar — vertical neutron-star beam with lateral pulses.
  pulsar: [
    "............",
    ".....##.....",
    "..#######...",
    "...######...",
    "...######...",
    "..#######...",
    ".....##.....",
    "............",
  ],
  // Black hole — dense ring with hollow accretion centre.
  blackHole: [
    "............",
    "...######...",
    "..##....##..",
    ".##......##.",
    ".##......##.",
    "..##....##..",
    "...######...",
    "............",
  ],
  // Upward-pointing arrow/comet
  arrowhead: [
    "............",
    ".....##.....",
    "....####....",
    "...######...",
    "..########..",
    ".##########.",
    "............",
    "............",
  ],
  rocket: [
    "............",
    ".....##.....",
    "....####....",
    "....####....",
    "...######...",
    "..##.##.##..",
    "..#......#..",
    "............",
  ],
  saucer: [
    "............",
    "............",
    "...######...",
    "..########..",
    ".##########.",
    "...#....#...",
    "............",
    "............",
  ],
  ringedPlanet: [
    "............",
    "....####....",
    "..########..",
    ".##########.",
    ".##########.",
    "..########..",
    "....####....",
    "............",
  ],
  comet: [
    "............",
    "........####",
    ".....#######",
    "...#########",
    ".###########",
    "............",
    "............",
    "............",
  ],
  star: [
    "............",
    ".....##.....",
    ".####..####.",
    "..########..",
    "...######...",
    "..##....##..",
    ".##......##.",
    "............",
  ],
  constellation: [
    "............",
    ".##......##.",
    "..#......#..",
    "....####....",
    "..#......#..",
    ".##......##.",
    "............",
    "............",
  ],
  galaxy: [
    "............",
    "...######...",
    ".###....####",
    ".##........#",
    ".#........##",
    "####....###.",
    "...######...",
    "............",
  ],
  satellite: [
    "............",
    "....#..#....",
    "....####....",
    ".##########.",
    "....####....",
    "....#..#....",
    "............",
    "............",
  ],
  crescent: [
    "............",
    "...######...",
    "..##....##..",
    ".##.........",
    ".##.........",
    "..##....##..",
    "...######...",
    "............",
  ],
  // Aliens-of-the-galaxy row — wide coverage for later-game sectors.
  invaders: [
    "............",
    ".##.##.##.##",
    "############",
    ".##########.",
    "..##.##.##..",
    "##........##",
    "............",
    "............",
  ],
  asteroidField: [
    "............",
    "..##..###...",
    "##..####..##",
    ".####...####",
    "##..#####..#",
    "...####..##.",
    "............",
    "............",
  ],
  astronaut: [
    "............",
    "....####....",
    "....####....",
    "...######...",
    "..########..",
    "...##..##...",
    "..##....##..",
    "............",
  ],
  // --- Reference-art shapes -------------------------------------------------
  // Comet Fishbone — fish silhouette with head/eye, central spine, dorsal
  // fin spikes, and tail fan. Inspired by the user-provided "Ruler of
  // the Cosmic Shoals" boss concept art. Reads as a recognizable fish
  // even in low-contrast lighting.
  cometFishbone: [
    "...##.......",
    ".######..#..",
    "###.####.##.",
    ".##########.",
    "############",
    ".##########.",
    "###.####.##.",
    ".######..#..",
  ],
  // UFO with landing-light ring — saucer dome + wide flat hull + 3
  // beam dots underneath. Reads instantly as "alien craft".
  ufo: [
    "............",
    "....####....",
    "...######...",
    "..########..",
    ".##########.",
    "............",
    "..#..##..#..",
    "............",
  ],
  // Space station — central hub with 4 arms (an X-shape Mir/ISS).
  spaceStation: [
    "............",
    ".##......##.",
    "..##....##..",
    "....####....",
    "...######...",
    "....####....",
    "..##....##..",
    ".##......##.",
  ],
  // --- Mini-boss stamps -----------------------------------------------------
  // Stylised compact versions of each sector boss. Smaller than the actual
  // boss-level layouts so pilots can stamp them anywhere on the 8×12 grid.
  // Tier markers in the boss levels themselves are still rendered via
  // `generateBossLevel` — these are pure-shape cells (no tier info), the
  // builder's active brush tier paints them.
  miniCradleSentinel: [
    "............",
    "....####....",
    "...######...",
    "...######...",
    "....####....",
    "............",
    "............",
    "............",
  ],
  miniAuroraHydra: [
    "............",
    ".#.#..#..#.#",
    "##.####.####",
    "############",
    ".##########.",
    "..########..",
    "...######...",
    "............",
  ],
  miniEmberLeviathan: [
    "............",
    "...########.",
    "..##########",
    ".####....###",
    "############",
    ".####....###",
    "..##########",
    "...########.",
  ],
  miniIronScourge: [
    "............",
    "..#......#..",
    ".###....###.",
    "####....####",
    "############",
    "############",
    ".####..####.",
    "..##....##..",
  ],
  miniWraithTyrant: [
    "....####....",
    "...######...",
    "..########..",
    ".####..####.",
    "###......###",
    ".####..####.",
    "..########..",
    "...######...",
  ],
  miniVoidApocalypse: [
    "###..####..#",
    "############",
    "##..####..##",
    "############",
    "############",
    "##..####..##",
    "############",
    "###..####..#",
  ],
};

function parseShape(raw) {
  const cells = [];
  for (let r = 0; r < raw.length; r++) {
    const row = raw[r];
    for (let c = 0; c < row.length; c++) {
      if (row[c] === "#") cells.push([r, c]);
    }
  }
  return cells;
}

// Human-readable shape names surfaced to the UI as a "shape callout" chip.
const SHAPE_LABELS = {
  diamond: "Diamond Invader",
  invaderStack: "Invader Stack",
  saturn: "Saturn Rings",
  nebula: "Nebula Cloud",
  pulsar: "Pulsar Beam",
  blackHole: "Black Hole",
  arrowhead: "Arrowhead",
  rocket: "Rocket",
  saucer: "Flying Saucer",
  ringedPlanet: "Ringed Planet",
  comet: "Comet Trail",
  star: "Five-Point Star",
  constellation: "Constellation",
  galaxy: "Galaxy Spiral",
  satellite: "Satellite",
  crescent: "Crescent Moon",
  invaders: "Alien Invaders",
  asteroidField: "Asteroid Field",
  astronaut: "Lone Astronaut",
  cometFishbone: "Comet Fishbone",
  ufo: "UFO Lander",
  spaceStation: "Space Station",
  miniCradleSentinel: "Boss · Cradle Sentinel",
  miniAuroraHydra: "Boss · Aurora Hydra",
  miniEmberLeviathan: "Boss · Ember Leviathan",
  miniIronScourge: "Boss · Iron Scourge",
  miniWraithTyrant: "Boss · Wraith Tyrant",
  miniVoidApocalypse: "Boss · Void Apocalypse",
};

const SHAPES = Object.fromEntries(
  Object.entries(SHAPES_RAW).map(([k, v]) => [k, parseShape(v)])
);

export function listShapes() {
  return Object.entries(SHAPE_LABELS).map(([id, label]) => ({
    id,
    label,
    cells: SHAPES[id],
  }));
}

// Curated shape pools per difficulty band. Higher levels get the dense /
// multi-element shapes that require more hits to clear. Each sector also has
// a SIGNATURE_SHAPES entry — one shape that ONLY appears in that sector so
// the sector-to-sector progression reads as visually distinct.
const SHAPE_POOLS = {
  beginner: ["diamond", "arrowhead", "crescent", "star", "astronaut", "saturn", "ufo"],
  easy:     ["rocket", "saucer", "ringedPlanet", "constellation", "star", "crescent", "saturn", "nebula", "ufo", "spaceStation"],
  medium:   ["rocket", "saucer", "ringedPlanet", "satellite", "comet", "galaxy", "nebula", "pulsar", "spaceStation"],
  hard:     ["invaders", "galaxy", "ringedPlanet", "asteroidField", "satellite", "rocket", "pulsar", "invaderStack", "cometFishbone", "spaceStation"],
  expert:   ["invaders", "asteroidField", "galaxy", "comet", "saucer", "diamond", "blackHole", "invaderStack", "cometFishbone"],
  legendary:["invaders", "asteroidField", "galaxy", "ringedPlanet", "comet", "diamond", "arrowhead", "blackHole", "pulsar", "invaderStack", "cometFishbone"],
};

// Each sector gets one exclusive "signature" that cannot be randomly picked in
// other sectors — gives Saturn-like unique sector identity.
const SIGNATURE_SHAPES = {
  beginner: "saturn",
  easy: "nebula",
  medium: "pulsar",
  hard: "invaderStack",
  expert: "blackHole",
  legendary: "invaders",
};

export function getSignatureShape(sectorId) {
  const id = SIGNATURE_SHAPES[sectorId];
  if (!id) return null;
  return { id, label: SHAPE_LABELS[id] || id, cells: SHAPES[id] };
}

// Public listing of every shape that can appear in a given sector — drives
// the sector-card teaser on /levels. Signature shape is deduped on top
// (first slot) and the rest of the pool follows in declared order. Each
// entry carries `{ id, label, isSignature }` so the renderer can emphasize
// the sector-exclusive shape.
export function getSectorShapes(sectorId) {
  const pool = SHAPE_POOLS[sectorId] || [];
  const signature = SIGNATURE_SHAPES[sectorId];
  const seen = new Set();
  const out = [];
  if (signature && SHAPE_LABELS[signature]) {
    out.push({ id: signature, label: SHAPE_LABELS[signature], isSignature: true });
    seen.add(signature);
  }
  for (const id of pool) {
    if (seen.has(id)) continue;
    if (!SHAPE_LABELS[id]) continue;
    out.push({ id, label: SHAPE_LABELS[id], isSignature: false });
    seen.add(id);
  }
  return out;
}

function mirrorCells(cells) {
  return cells.map(([r, c]) => [r, COLS - 1 - c]);
}

function shiftCells(cells, dr, dc) {
  return cells.map(([r, c]) => [r + dr, c + dc]);
}
// --- Boss levels -----------------------------------------------------------
// Sector-end boss fights at levels 500, 1000, 1500, 2000, 2500, 3000.
// Each boss is a dense 5×5 high-tier cluster centered horizontally near the
// top, surrounded by a sparse defensive ring at the bottom edges. The
// `boss` metadata drives the HUD HP bar + name banner.

const BOSS_LEVELS = new Set([500, 1000, 1500, 2000, 2500, 3000]);

const BOSS_NAMES = {
  beginner:  "Cradle Sentinel",
  easy:      "Aurora Hydra",
  medium:    "Ember Leviathan",
  hard:      "Iron Scourge",
  expert:    "Wraith Tyrant",
  legendary: "Void Apocalypse",
};

export function isBossLevel(globalLevel) {
  return BOSS_LEVELS.has(globalLevel);
}

function generateBossLevel(globalLevel, sector) {
  // 8 rows × 12 cols. Boss cluster sits at rows 0-4, cols 3-9 (5×7 = 35
  // cells max, sculpted into a diamond-ish silhouette).
  const grid = Array.from({ length: 8 }, () => Array(12).fill(0));

  // Boss body — symmetric "fortress" silhouette. Inner steel core, outer
  // expert/medium ring. Empty corners give the cluster its boss shape.
  // Tier legend: 4 = steel (3hp), 3 = expert pink (2hp), 2 = gold (1hp), 1 = cyan (1hp)
  const body = [
    "...3333...",
    "..344443..",
    ".34444443.",
    "..344443..",
    "...3333...",
  ];
  // Anchor the body so the 10-char rows fall at columns 1..10 (centered in 12 cols).
  for (let r = 0; r < body.length; r++) {
    const row = body[r];
    for (let c = 0; c < row.length; c++) {
      const ch = row[c];
      if (ch === "." || ch === " ") continue;
      const tier = parseInt(ch, 10);
      if (Number.isFinite(tier) && tier >= 1 && tier <= 4) {
        grid[r][c + 1] = tier;
      }
    }
  }

  // Defensive flanks at row 6 — a thin guarded line the ball must penetrate
  // before reaching the boss core.
  for (let c = 0; c < 12; c++) {
    if (c === 0 || c === 11) continue;
    grid[6][c] = (c % 3 === 0) ? 2 : 1;
  }

  // Compute total HP across all blocks (cyan/gold = 1, expert = 2, steel = 3).
  // The HUD HP bar is computed off this number so depleting matches reality.
  const TIER_HP = [0, 1, 1, 2, 3];
  let totalHp = 0;
  for (const row of grid) for (const v of row) if (v) totalHp += TIER_HP[v];

  return {
    ballSpeed: sector.speedRange[1],   // boss ball speed = top end of sector range
    grid,
    name: BOSS_NAMES[sector.id] || "Sector Boss",
    sectorId: sector.id,
    globalLevel,
    shapes: ["Boss"],
    boss: {
      name: BOSS_NAMES[sector.id] || "Sector Boss",
      totalHp,
    },
  };
}

export function getLevel(globalLevel) {
  // Boss levels short-circuit the procedural generator with a hand-tuned
  // fortress layout + boss metadata for the HUD HP bar.
  if (isBossLevel(globalLevel)) {
    return generateBossLevel(globalLevel, getSector(globalLevel));
  }
  return _getLevelProcedural(globalLevel);
}

function _getLevelProcedural(globalLevel) {
  const sector = getSector(globalLevel);
  const localIdx = globalLevel - sector.from; // 0..499
  const t = localIdx / (SECTOR_SIZE - 1); // 0..1 progress in sector
  const rand = mulberry32(seedFromString(`cwb-${sector.id}-${globalLevel}`));

  const ballSpeed = lerp(sector.speedRange[0], sector.speedRange[1], t);
  // Density controls shape-cell survival — higher t keeps more cells filled.
  const density = lerp(sector.densityRange[0], sector.densityRange[1], t);

  // Number of overlaid space shapes per level: 1 early, 2 mid, 2–3 late.
  const sectorDifficultyBoost = sector.from > 1500 ? 0.25 : 0;
  const shapeCountFloat = lerp(1.0, 2.8, Math.min(1, t + sectorDifficultyBoost));
  const shapeCount = Math.max(1, Math.round(shapeCountFloat + (rand() - 0.5) * 0.4));
  const pool = SHAPE_POOLS[sector.id] || SHAPE_POOLS.beginner;

  // Tier weight shifting — more bosses & pink bricks as t→1. Blends the
  // sector's baseline tierWeights toward a "late-game" profile that doubles
  // tier-4 (boss) probability at the back of a sector.
  const lateWeights = [0.1, 0.2, 0.4, 0.3];
  const tw = sector.tierWeights.map((base, i) =>
    base * (1 - t * 0.6) + lateWeights[i] * t * 0.6
  );

  // Shape selection: always include this sector's SIGNATURE shape plus
  // (shapeCount - 1) extras picked from the sector pool. Late in the sector
  // we also allow a "boss" shape (invaderStack / blackHole / galaxy) to
  // overlay a second time for extra density.
  const signature = SIGNATURE_SHAPES[sector.id];
  const picks = [];
  if (signature) picks.push(signature);
  while (picks.length < shapeCount) {
    picks.push(pool[Math.floor(rand() * pool.length)]);
  }

  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill(0));
  const usedShapes = [];
  const usedShapeIds = []; // canonical ids — server uses these for achievements

  for (let i = 0; i < picks.length; i++) {
    const name = picks[i];
    let cells = SHAPES[name] || SHAPES.diamond;
    if (rand() < 0.5) cells = mirrorCells(cells);
    // Small random vertical shift (±1 row) — keeps bottom two rows paddle-safe.
    const dr = Math.round((rand() - 0.5) * 1.2);
    cells = shiftCells(cells, dr, 0);
    for (const [r, c] of cells) {
      if (r < 0 || r >= ROWS - 1 || c < 0 || c >= COLS) continue;
      // Survival roll — higher density (late game) keeps more cells.
      if (rand() > density) continue;
      grid[r][c] = weightedTier(rand, tw);
    }
    usedShapes.push(SHAPE_LABELS[name] || name);
    usedShapeIds.push(name);
  }

  // Late-game noise: sprinkle extra tier bricks around the shapes in
  // Expert / Legendary sectors so players never see a "clean" shape.
  if (sector.from >= 1501) {
    const noiseCount = Math.round(lerp(2, 8, t));
    for (let i = 0; i < noiseCount; i++) {
      const r = 1 + Math.floor(rand() * (ROWS - 3));
      const c = Math.floor(rand() * COLS);
      if (grid[r][c] === 0) {
        grid[r][c] = weightedTier(rand, tw);
      }
    }
  }

  // Safety: if the shape masks collapsed to fewer than 6 blocks, fall back
  // to a dense band on row 2 so the level is still playable.
  let blockCount = 0;
  for (const row of grid) for (const v of row) if (v) blockCount++;
  if (blockCount < 6) {
    for (let c = 1; c < COLS - 1; c++) {
      grid[2][c] = weightedTier(rand, tw);
    }
  }

  return {
    ballSpeed,
    grid,
    name: getLevelName(globalLevel, sector),
    sectorId: sector.id,
    globalLevel,
    shapes: Array.from(new Set(usedShapes)),
    // Canonical shape ids for backend tracking (Shape Master achievement).
    // Distinct from `shapes` (user-facing labels) so server has a stable
    // key to dedupe per-shape clears.
    shapeIds: Array.from(new Set(usedShapeIds)),
  };
}

// --- Level name generator ---------------------------------------------------
// Procedural and DETERMINISTIC: a given globalLevel always returns the same
// name, even across reloads / devices / users, so leaderboards and shared
// links read consistently. Uses a *bijective* mapping per sector so all 500
// levels within a sector get a unique (adjective, noun) pair, and the
// adjective pools are disjoint across sectors so every name in the 3000-
// level catalog is globally unique.

// Disjoint adjective pools (no overlaps between sectors), 20 each = 120 total.
const NAME_ADJECTIVES = {
  beginner:  ["Stellar", "Lunar", "Astral", "Celestial", "Aurora", "Crystal", "Prism", "Halo", "Polar", "Tidal", "Twilight", "Dawn", "Drifting", "Nebular", "Frozen", "Whisper", "Quiet", "Pearl", "Mira", "Cradle"],
  easy:      ["Verdant", "Emerald", "Cosmic", "Nova", "Pulsar", "Quartz", "Photon", "Echo", "Wandering", "Charted", "Radiant", "Glittering", "Quasar", "Soaring", "Phantom", "Boreal", "Spectral", "Helix", "Beacon", "Vector"],
  medium:    ["Solar", "Plasma", "Comet", "Rogue", "Reaver", "Searing", "Burning", "Ember", "Magma", "Cyclone", "Vortex", "Surge", "Riptide", "Maelstrom", "Eclipse", "Storm", "Forge", "Sundered", "Pyre", "Kiln"],
  hard:      ["Scorched", "Fractured", "Crimson", "Howling", "Cinder", "Razor", "Volcanic", "Demolisher", "Onslaught", "Raging", "Splintered", "Charred", "Brutal", "Savage", "Iron", "Ferrous", "Reckless", "Dread", "Hollow", "Spectre"],
  expert:    ["Doomed", "Eldritch", "Crucible", "Calamity", "Mortal", "Bleeding", "Shattered", "Catastrophe", "Cataclysm", "Annihilator", "Banshee", "Wraith", "Carnage", "Devastator", "Inferno", "Tempest", "Marauder", "Ravager", "Predator", "Vandal"],
  legendary: ["Void", "Eternal", "Apocalypse", "Singular", "Abyssal", "Omega", "Genesis", "Ascendant", "Transcendent", "Final", "Celestium", "Empyrean", "Forsaken", "Primordial", "Infinite", "Sovereign", "Imperial", "Halcyon", "Phoenix", "Ragnarok"],
};

// Shared noun pool — 50 entries. With 20 adjectives × 50 nouns = 1000 unique
// pairs available per sector, comfortably above the 500-level requirement.
const NAME_NOUNS = [
  "Drift", "Reach", "Belt", "Cluster", "Veil", "Spire", "Gate", "Edge", "Tide",
  "Field", "Cradle", "Pulse", "Span", "Run", "Trial", "Crossing", "Passage",
  "Approach", "Threshold", "Horizon", "Frontier", "Expanse", "Echoes", "Wake",
  "Ascent", "Descent", "Bastion", "Citadel", "Sanctum", "Vault", "Forge",
  "Maw", "Jaws", "Spine", "Crown", "Hollow", "Rift", "Maze", "Labyrinth",
  "Sprawl", "Reaches", "Outpost", "Beacon", "Anchorage", "Drydock", "Course",
  "Strait", "Channel", "Quarry", "Atrium",
];

// Stride coprime with 1000 (= 20 × 50) so multiplying by it permutes the
// (adj, noun) index space bijectively — guarantees 500 unique pairs in each
// sector. 251 is prime and gcd(251, 1000) = 1.
const NAME_STRIDE = 251;

// Per-sector offset so the cycling order shifts between sectors and the
// names don't all start with the same adjective at level boundaries.
const SECTOR_OFFSETS = {
  beginner: 0,
  easy: 137,
  medium: 313,
  hard: 521,
  expert: 743,
  legendary: 911,
};

export function getLevelName(globalLevel, sector) {
  const s = sector || getSector(globalLevel);
  const adjPool = NAME_ADJECTIVES[s.id] || NAME_ADJECTIVES.beginner;
  const localIdx = globalLevel - s.from; // 0 .. 499
  const aLen = adjPool.length;            // 20
  const nLen = NAME_NOUNS.length;         // 50
  const offset = SECTOR_OFFSETS[s.id] || 0;
  // Bijective scramble within the sector's (adj × noun) space.
  const idx = (localIdx * NAME_STRIDE + offset) % (aLen * nLen);
  const adjIdx = idx % aLen;
  const nounIdx = ((idx / aLen) | 0) % nLen;
  return `${adjPool[adjIdx]} ${NAME_NOUNS[nounIdx]}`;
}

// --- Endless Mode ----------------------------------------------------------
// Wave-based survival run: each wave is a procedurally generated layout that
// scales in difficulty as the wave number climbs. Visuals cycle through the
// 6 sectors (every 5 waves bumps to the next sector aesthetic so the
// progression reads as a tour through the cosmos). Ball speed and tier
// distribution ramp linearly from beginner-easy to legendary-tough across
// waves 1..50; beyond wave 50 the difficulty plateaus at the Legendary
// late-game profile so the run remains physically clearable.
export function getEndlessWave(wave) {
  const w = Math.max(1, Math.floor(wave));
  // Sector cycling: every 5 waves moves through Beginner → … → Legendary,
  // then loops at Legendary so wave 30+ all share the legendary palette.
  const sectorIdx = Math.min(SECTORS.length - 1, Math.floor((w - 1) / 5));
  const sector = SECTORS[sectorIdx];
  const localT = ((w - 1) % 5) / 4;                // 0..1 within current 5-wave block
  const globalT = Math.min(1, (w - 1) / 49);       // 0..1 across the first 50 waves

  const ballSpeed = lerp(4.5, 9.5, globalT);
  const density = lerp(sector.densityRange[0], sector.densityRange[1], localT);
  const lateWeights = [0.05, 0.15, 0.45, 0.35];
  const rawTw = sector.tierWeights.map((b, i) => b * (1 - globalT) + lateWeights[i] * globalT);
  const swSum = rawTw.reduce((a, b) => a + b, 0) || 1;
  const tw = rawTw.map((v) => v / swSum);

  const rand = mulberry32(seedFromString(`cwb-endless-${w}`));
  // 1 shape early, 2 mid, 3 late — keeps the field cleanly readable but
  // gradually stacks more shrapnel as waves stack up.
  const shapeCount = w < 5 ? 1 : (w < 15 ? 2 : 3);
  const pool = SHAPE_POOLS[sector.id] || SHAPE_POOLS.beginner;
  const picks = [];
  const signature = SIGNATURE_SHAPES[sector.id];
  if (signature) picks.push(signature);
  while (picks.length < shapeCount) {
    picks.push(pool[Math.floor(rand() * pool.length)]);
  }

  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill(0));
  const usedShapes = [];
  for (const name of picks) {
    let cells = SHAPES[name] || SHAPES.diamond;
    if (rand() < 0.5) cells = mirrorCells(cells);
    const dr = Math.round((rand() - 0.5) * 1.2);
    cells = shiftCells(cells, dr, 0);
    for (const [r, c] of cells) {
      if (r < 0 || r >= ROWS - 1 || c < 0 || c >= COLS) continue;
      if (rand() > density) continue;
      grid[r][c] = weightedTier(rand, tw);
    }
    usedShapes.push(SHAPE_LABELS[name] || name);
  }

  // Safety: ensure at least 6 blocks so the wave is clearable.
  let cnt = 0;
  for (const row of grid) for (const v of row) if (v) cnt++;
  if (cnt < 6) {
    for (let c = 1; c < COLS - 1; c++) {
      grid[2][c] = weightedTier(rand, tw);
    }
  }

  return {
    ballSpeed,
    grid,
    name: `Wave ${w} · ${sector.name}`,
    sectorId: sector.id,
    globalLevel: w,
    shapes: Array.from(new Set(usedShapes)),
    isEndless: true,
    wave: w,
  };
}


// --- Daily/challenge helpers stay ------------------------------------------
export function generateDailyLevel(isoDate) {
  const rand = mulberry32(seedFromString("cwb-daily-" + isoDate));
  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill(0));
  const rows = 5 + Math.floor(rand() * 3);
  const bossChance = 0.04 + rand() * 0.05;
  for (let r = 1; r <= rows; r++) {
    for (let c = 1; c < COLS - 1; c++) {
      const roll = rand();
      if (roll < 0.18) continue;
      if (roll < 0.18 + bossChance) grid[r][c] = 4;
      else if (roll < 0.5) grid[r][c] = 3;
      else if (roll < 0.8) grid[r][c] = 2;
      else grid[r][c] = 1;
    }
  }
  const ballSpeed = 5.5 + rand() * 1.5;
  return { ballSpeed, grid, name: `Daily · ${isoDate}`, isDaily: true, seed: isoDate };
}
